/**
 * GLOBAL AUTH INTERCEPTOR — attaches the login JWT to every backend call.
 *
 * WHY THIS IS GLOBAL AND NOT PER-CALLER
 * `/svc/*` used to be protected by Caddy basic_auth: the browser popped a username box,
 * then sent those credentials automatically on every request, so no code ever had to
 * think about auth. Removing that popup (it was also blocking the real login page) moved
 * `/svc/*` behind the gateway's JWT — and instantly broke every raw `fetch('/svc/...')`
 * with 401, because the token lives in localStorage and nothing attached it.
 *
 * Those calls are spread across candleCache, CommandHeader, CommandDashboard,
 * ServiceHealthContext, LearningLab, DreamEngineViewer, SimulationLab and more. Patching
 * them one at a time guarantees one gets missed and silently 401s — which is exactly how
 * the chart went blank. Patch `fetch` itself, once, and every caller is covered, including
 * ones written later.
 *
 * Only same-origin backend paths get the header: never leak the token to a third party.
 */

// The app persists the token under several different keys depending on which module
// wrote it ('auth_token' is the one login actually writes; other code reads
// 'access_token' and finds nothing). Accept any of them rather than silently send none.
const TOKEN_KEYS = ['auth_token', 'access_token', 'accessToken', 'token'] as const;

function readToken(): string | null {
  for (const k of TOKEN_KEYS) {
    const t = localStorage.getItem(k);
    if (t) return t;
  }
  return null;
}

/** Backend paths that sit behind the gateway's JWT. */
function needsAuth(url: string): boolean {
  try {
    // relative ('/svc/x') or absolute on our own origin
    const path = url.startsWith('http') ? new URL(url).pathname : url;
    const sameOrigin = !url.startsWith('http') || new URL(url).origin === window.location.origin;
    return sameOrigin && (path.startsWith('/svc/') || path.startsWith('/api/'));
  } catch {
    return false;
  }
}

export function installAuthFetch(): void {
  const original = window.fetch.bind(window);

  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    const url =
      typeof input === 'string' ? input
      : input instanceof URL ? input.toString()
      : input.url;

    if (!needsAuth(url)) return original(input, init);

    const token = readToken();
    if (!token) return original(input, init);   // logged out: let it 401 honestly

    const headers = new Headers(
      init?.headers ?? (input instanceof Request ? input.headers : undefined),
    );
    // never clobber a caller that set its own Authorization
    if (!headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);

    return original(input, { ...init, headers });
  };
}
